import React, { useState, useEffect } from "react";
import { UserPlus, Calendar, Phone, MessageSquare, AlertCircle, Send, Users, Shield, Plus, CheckCircle, RefreshCw, X, Printer } from "lucide-react";
import { Patient, Appointment, WhatsAppChat, FinancialRecord, Installment } from "../types";
import StatsBanner from "./StatsBanner";
import GoogleCalendarSync from "./GoogleCalendarSync";
import PatientEhrPrintPreview from "./PatientEhrPrintPreview";
import SendWhatsAppButton from "./SendWhatsAppButton";
import { connectSocket } from "../utils/socket";

interface ReceptionDashboardProps {
  user: { email: string; name: string; role: string };
  apiToken: string;
}

export default function ReceptionDashboard({ user, apiToken }: ReceptionDashboardProps) {
  // Online Status
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    loadFinancialLedger();
    const handleLedgerUpdate = () => {
      loadFinancialLedger();
    };
    window.addEventListener("storage", handleLedgerUpdate);
    window.addEventListener("clinical_ledger_updated" as any, handleLedgerUpdate);
    return () => {
      window.removeEventListener("storage", handleLedgerUpdate);
      window.removeEventListener("clinical_ledger_updated" as any, handleLedgerUpdate);
    };
  }, []);

  const loadFinancialLedger = () => {
    try {
      const stored = localStorage.getItem("iq_doctor_standalone_ledger");
      if (stored) {
        setFinancials(JSON.parse(stored));
      }
    } catch (err) {
      console.error("Clinical System: Corrupted ledger registry.", err);
    }
  };

  const [patients, setPatients] = useState<Patient[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [whatsappChats, setWhatsappChats] = useState<WhatsAppChat[]>([]);
  const [financials, setFinancials] = useState<FinancialRecord[]>([]);
  const [alerts, setAlerts] = useState<string[]>([]);
  const [selectedChat, setSelectedChat] = useState<WhatsAppChat | null>(null);

  // Form Inputs
  const [receptionistSearch, setReceptionistSearch] = useState("");
  const [newName, setNewName] = useState("");
  const [newNationalId, setNewNationalId] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newDob, setNewDob] = useState("1995-01-01");
  const [newGender, setNewGender] = useState("");
  const [newAge, setNewAge] = useState("");
  const [newBloodType, setNewBloodType] = useState("");
  const [newAllergies, setNewAllergies] = useState("");

  // EXPERT COMMENTARY: Auto-detect gender based on Arabic name characteristics after first name is typed
  useEffect(() => {
    // Only attempt auto-detect if gender is not manually set yet AND user has typed at least one space (finished first name)
    if (!newName || newGender || !newName.includes(" ")) return;
    
    const firstName = newName.trim().split(/\s+/)[0];
    if (!firstName) return;

    const maleExceptions = ["حمزة", "اسامة", "أسامة", "حذيفة", "معاوية", "عطية", "خليفة", "يحيى", "موسى", "عيسى", "مصطفى", "مرتضى", "مجتبى", "رضا", "علاء", "ضياء", "بهاء", "عيسى"];
    const femaleNames = ["مريم", "نور", "شهد", "بنين", "رسل", "ملاك", "زينب", "سعاد", "وداد", "كوثر", "ايات", "آيات", "يقين", "غدير", "عبير", "حنان", "إيمان", "ايمان", "انعام", "أحلام", "امنة", "آمنة"];
    
    if (maleExceptions.includes(firstName)) {
      setNewGender("ذكر");
    } else if (femaleNames.includes(firstName)) {
      setNewGender("أنثى");
    } else if (firstName.endsWith("ة") || firstName.endsWith("اء") || firstName.endsWith("ى") || firstName.endsWith("ا")) {
      setNewGender("أنثى");
    } else {
      setNewGender("ذكر");
    }
  }, [newName, newGender]);

  /* Expert Commentary: Automatically updates calculated age when date of birth changes via birth diary calendar selection */
  useEffect(() => {
    try {
      if (newDob) {
        const birthDate = new Date(newDob);
        if (!isNaN(birthDate.getTime())) {
          const today = new Date();
          let calculatedAge = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            calculatedAge--;
          }
          setNewAge(Math.max(0, calculatedAge).toString());
        }
      }
    } catch (error) {
      console.error("Clinical System Alert: Failed to auto-calculate birth age metrics.", error);
    }
  }, [newDob]);

  const [bookPatientId, setBookPatientId] = useState("");
  const [bookPhysician, setBookPhysician] = useState("mytradefuture@gmail.com");
  const [bookDept, setBookDept] = useState<"GP" | "DERMATOLOGY">("DERMATOLOGY");
  const [bookTime, setBookTime] = useState("");

  // EXPERT COMMENTARY: New additive states to allocate physician, department and optional
  // appointment time directly during initial demographics intake form subscription.
  const [intakePhysician, setIntakePhysician] = useState("mytradefuture@gmail.com");
  const [intakeDept, setIntakeDept] = useState<"GP" | "DERMATOLOGY">("DERMATOLOGY");
  const [intakeDate, setIntakeDate] = useState("");
  const [intakeTimeBlock, setIntakeTimeBlock] = useState("");
  const [pendingPrintQuestion, setPendingPrintQuestion] = useState<{patientName: string, dob: string, dept: string, time: string} | null>(null);

  // Additive: states for editing/deleting appointments in active queue
  const [editingApptId, setEditingApptId] = useState<string | null>(null);
  const [editTime, setEditTime] = useState("");
  const [editStatus, setEditStatus] = useState<"PENDING" | "COMPLETED" | "MISSED">("PENDING");
  const [editPhysician, setEditPhysician] = useState("");
  const [editDept, setEditDept] = useState<"GP" | "DERMATOLOGY">("DERMATOLOGY");

  // Additive: state for editing patient name
  const [editingPatientId, setEditingPatientId] = useState<string | null>(null);
  const [editPatientName, setEditPatientName] = useState("");
  const [editPatientPhone, setEditPatientPhone] = useState("");
  const [editPatientNationalId, setEditPatientNationalId] = useState("");
  const [editPatientGender, setEditPatientGender] = useState("");
  const [editPatientDob, setEditPatientDob] = useState("");
  const [editPatientBloodType, setEditPatientBloodType] = useState("");
  const [editPatientAllergies, setEditPatientAllergies] = useState("");

  // Additive: states for the 10-minute interval clinic diary for future registrations only
  const [selectedDiaryDate, setSelectedDiaryDate] = useState<string>("");
  const [showEmptyDiarySlots, setShowEmptyDiarySlots] = useState<boolean>(false);

  // CLINICAL FEATURE: Secure manual Telegram dispatch state & helper function
  // Expert Commentary: Tracks Telegram invitation/reminders push statuses to avoid network lockups or double clicks on clinical screens.
  const [tgStatus, setTgStatus] = useState<{[patientId: string]: 'loading' | 'success' | 'error' | null}>({});
  const handleSendTelegram = async (patientId: string) => {
    if (!patientId) return;
    setTgStatus(prev => ({ ...prev, [patientId]: 'loading' }));
    try {
      const res = await fetch(`/api/patients/${patientId}/notify-telegram`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${apiToken}`,
          "Content-Type": "application/json"
        }
      });
      if (res.ok) {
        setTgStatus(prev => ({ ...prev, [patientId]: 'success' }));
        setTimeout(() => {
          setTgStatus(prev => ({ ...prev, [patientId]: null }));
        }, 3000);
      } else {
        setTgStatus(prev => ({ ...prev, [patientId]: 'error' }));
        setTimeout(() => {
          setTgStatus(prev => ({ ...prev, [patientId]: null }));
        }, 3000);
      }
    } catch (err) {
      console.error("[Clinical Tech Warning] Failed to manual dispatch telegram alert:", err);
      setTgStatus(prev => ({ ...prev, [patientId]: 'error' }));
      setTimeout(() => {
        setTgStatus(prev => ({ ...prev, [patientId]: null }));
      }, 3000);
    }
  };

  /* Expert Commentary: Initialize default intake time to local current timing for the next instant visit queue */
  useEffect(() => {
    try {
      const now = new Date();
      const offset = now.getTimezoneOffset() * 60000;
      const localDateIso = new Date(now.getTime() - offset).toISOString().slice(0, 10);
      setIntakeDate(localDateIso);
    } catch (e) {
      console.error("Clinical System Alert: Failed to default current date-time value.", e);
    }
  }, []);

  /* Expert Commentary: Automatically sets the default selected diary date to the first date with upcoming registrations */
  useEffect(() => {
    try {
      if (appointments && appointments.length > 0) {
        const now = new Date();
        const futureAppts = appointments.filter(a => new Date(a.dateTime) > now);
        if (futureAppts.length > 0) {
          const uniqueDates = Array.from(new Set(futureAppts.map(a => {
            const d = new Date(a.dateTime);
            return d.toLocaleDateString("en-CA"); // YYYY-MM-DD
          }))).sort();
          if (uniqueDates.length > 0 && !selectedDiaryDate) {
            setSelectedDiaryDate(uniqueDates[0]);
          }
        } else if (!selectedDiaryDate) {
          const offset = now.getTimezoneOffset() * 60000;
          const localDateStr = new Date(now.getTime() - offset).toISOString().slice(0, 10);
          setSelectedDiaryDate(localDateStr);
        }
      }
    } catch (err) {
      console.error("Clinical System Info: Failed to select standard default clinic diary date.", err);
    }
  }, [appointments, selectedDiaryDate]);

  const [manualReply, setManualReply] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Iraqi National Unified ID registry integration (وزيرية الأحوال المدنية)
  const [isVerifyingIraqiId, setIsVerifyingIraqiId] = useState(false);
  const [iraqiVerificationData, setIraqiVerificationData] = useState<any | null>(null);
  const [iraqiVerificationError, setIraqiVerificationError] = useState<string | null>(null);

  const handleVerifyIraqiId = async () => {
    if (!newNationalId) {
      setIraqiVerificationError("أدخل رقم البطاقة الوطنية للتحقق.");
      return;
    }
    const cleanId = newNationalId.replace(/[\s-]/g, "");
    if (!/^\d{12}$/.test(cleanId)) {
      setIraqiVerificationError("رقم البطاقة الوطنية يجب أن يكون 12 رقماً فقط.");
      return;
    }

    setIsVerifyingIraqiId(true);
    setIraqiVerificationError(null);
    setIraqiVerificationData(null);
    try {
      const response = await fetch("/api/verify-iraqi-national-id", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          nationalId: cleanId,
          preferredName: newName 
        })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setIraqiVerificationData(result);
        setNewName(result.fullName);
        setNewDob(result.dob);
        setNewGender(result.gender);
        const birthYear = new Date(result.dob).getFullYear();
        const currentYear = new Date().getFullYear();
        setNewAge((currentYear - birthYear).toString());
      } else {
        setIraqiVerificationError(result.error || "فشل التحقق من قاعدة بيانات البطاقة الموحدة.");
      }
    } catch (err) {
      console.error(err);
      setIraqiVerificationError("حدث خطأ في الاتصال بنظام السجل المدني العراقي الموحد.");
    } finally {
      setIsVerifyingIraqiId(false);
    }
  };

  // Iraqi joint telecoms subscriber registry state (دليل هواتف العراق المشترك - زين، آسيا سيل، كورك)
  const [isVerifyingIraqiPhone, setIsVerifyingIraqiPhone] = useState(false);
  const [iraqiPhoneData, setIraqiPhoneData] = useState<any | null>(null);
  const [iraqiPhoneError, setIraqiPhoneError] = useState<string | null>(null);

  const handleVerifyIraqiPhone = async () => {
    if (!newPhone) {
      setIraqiPhoneError("أدخل رقم الهاتف أولاً للتحقق.");
      return;
    }
    const cleanPhone = newPhone.replace(/[\s\-\(\)\+]/g, "");
    setIsVerifyingIraqiPhone(true);
    setIraqiPhoneError(null);
    setIraqiPhoneData(null);
    try {
      const response = await fetch("/api/verify-iraqi-phone", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          phone: cleanPhone,
          preferredName: newName 
        })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setIraqiPhoneData(result);
        if (result.subscriberName) {
          setNewName(result.subscriberName);
        }
      } else {
        setIraqiPhoneError(result.error || "بيانات غير متوفرة في الدليل العراقي المشترك.");
      }
    } catch (err) {
      console.error(err);
      setIraqiPhoneError("حدث خطأ أثناء الربط مع دليل أرقام الهواتف.");
    } finally {
      setIsVerifyingIraqiPhone(false);
    }
  };

  // Direct simulator for testing the WhatsApp Bot instantly inside the browser
  const [testSimPhone, setTestSimPhone] = useState("+964 770 123 4567");
  const [testSimMessage, setTestSimMessage] = useState("1");
  const [testSimName, setTestSimName] = useState("Ahmed Al-Khafaji");

  const [printingWristband, setPrintingWristband] = useState<{patientName: string, dob: string, dept: string, time: string} | null>(null);
  const [printingBill, setPrintingBill] = useState<Patient | null>(null);

  useEffect(() => {
    fetchData();
    // Poll chat records every 4 seconds for immediate live reaction simulation
    const interval = setInterval(fetchChatsOnly, 4000);

    const socket = connectSocket();
    socket.on("databaseSynchronised", () => {
      fetchData();
    });

    return () => {
      clearInterval(interval);
      socket.disconnect();
    };
  }, [apiToken]);

  const fetchData = async () => {
    if (!apiToken) return;
    try {
      const pRes = await fetch("/api/patients", { headers: { Authorization: `Bearer ${apiToken}` } });
      const pData = await pRes.json();
      if (pRes.ok) {
        setPatients(pData);
        if (pData.length > 0 && !bookPatientId) setBookPatientId(pData[0].id);
      }

      const aRes = await fetch("/api/appointments", { headers: { Authorization: `Bearer ${apiToken}` } });
      const aData = await aRes.json();
      if (aRes.ok) setAppointments(aData);

      await fetchChatsOnly();
    } catch (err) {
      console.error(err);
    }
  };

  const fetchChatsOnly = async () => {
    try {
      const cRes = await fetch("/api/whatsapp/chats");
      const cData = await cRes.json();
      if (cRes.ok) {
        setWhatsappChats(cData.chats);
        setAlerts(cData.notifications);

        // Keep active chat update synced
        if (selectedChat) {
          const fresh = cData.chats.find((c: WhatsAppChat) => c.phone === selectedChat.phone);
          if (fresh) setSelectedChat(fresh);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Helper to normalize Arabic characters for robust comparison to avoid double registrations
  const normalizeArabic = (str: string) => {
    if (!str) return "";
    return str.trim().toLowerCase()
      .replace(/[أإآا]/g, "ا")
      .replace(/ة/g, "ه")
      .replace(/ى/g, "ي")
      .replace(/[\s-]/g, "");
  };

  const handleOneClickCheckIn = async (p: Patient) => {
    try {
      // Calculate updated age based on DOB
      let calculatedAge = p.age || 35;
      if (p.dob) {
        const birthDate = new Date(p.dob);
        if (!isNaN(birthDate.getTime())) {
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          calculatedAge = Math.max(0, age);
        }
      }

      // 1. Update patient age via PUT /api/patients/:id
      const updateRes = await fetch(`/api/patients/${p.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`
        },
        body: JSON.stringify({ age: calculatedAge })
      });

      let updatedPatient = p;
      if (updateRes.ok) {
        updatedPatient = await updateRes.json();
        setPatients(prev => prev.map(item => item.id === p.id ? updatedPatient : item));
      }

      // Populate form
      setNewName(updatedPatient.name);
      setNewPhone(updatedPatient.phone);
      setNewDob(updatedPatient.dob || "1995-01-01");
      setNewNationalId(updatedPatient.nationalId || "");
      setNewGender(updatedPatient.gender || "");
      setNewAge(updatedPatient.age?.toString() || "");
      setNewBloodType(updatedPatient.bloodType || "");
      setNewAllergies(Array.isArray(updatedPatient.allergies) ? updatedPatient.allergies.join(", ") : "");
      setReceptionistSearch("");
      
      triggerToast(`تم جلب وتحديث عمر المريض "${updatedPatient.name}". يرجى اختيار وقت الجلسة والنقر على (Register) لإتمام الحجز.`);
    } catch (err) {
      console.error("Clinical System Error: One-click check-in failed:", err);
    }
  };

  // Additive: Secure clinical endpoint request handler to update/save edited appointment details
  const handleUpdateAppointment = async (apptId: string) => {
    try {
      if (!editTime) {
        triggerToast("يرجى اختيار وقت وتاريخ مناسبين للزيارة.");
        return;
      }

      const res = await fetch(`/api/appointments/${apptId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`
        },
        body: JSON.stringify({
          dateTime: new Date(editTime).toISOString(),
          status: editStatus,
          physicianId: editPhysician,
          department: editDept
        })
      });

      if (res.ok) {
        const updated = await res.json();
        setAppointments(prev => prev.map(a => a.id === apptId ? updated : a));
        setEditingApptId(null);
        triggerToast("تم تعديل تفاصيل وموعد زيارة المريض بنجاح!");
      } else {
        const err = await res.json();
        triggerToast(`خطأ: ${err.error || "فشل تحديث البيانات"}`);
      }
    } catch (err) {
      console.error("Clinical System Error: Failed to update appointment:", err);
      triggerToast("خطأ بالخادم أثناء محاولة تعديل الزيارة.");
    }
  };

  // Additive: Secure function to delete/cancel a logged appointment/visit
  const handleDeleteAppointment = async (apptId: string) => {
    try {
      const res = await fetch(`/api/appointments/${apptId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${apiToken}`
        }
      });

      if (res.ok) {
        setAppointments(prev => prev.filter(a => a.id !== apptId));
        triggerToast("تم حذف وإلغاء زيارة المريض من قائمة الانتظار بنجاح.");
      } else {
        const err = await res.json();
        triggerToast(`خطأ: ${err.error || "فشل حذف الزيارة"}`);
      }
    } catch (err) {
      console.error("Clinical System Error: Failed to delete appointment:", err);
      triggerToast("خطأ بالخادم أثناء محاولة حذف زيارة المريض.");
    }
  };

  const handleRegisterPatient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName || !newPhone || !newDob) return;

    // 1. Triple Name Validation
    const nameTrimmed = (newName || "").trim();
    const nameParts = nameTrimmed.split(/\s+/).filter(part => part.length >= 2);
    if (nameParts.length < 3) {
      triggerToast("خطأ: يرجى كتابة الاسم الثلاثي الصحيح بالكامل باللغة العربية (مثال: أحمد عباس السوداني)");
      return;
    }

    // 2. National Unified ID Card Validation
    if (newNationalId) {
      const cleanId = newNationalId.replace(/[\s-]/g, "");
      if (!/^\d{12}$/.test(cleanId)) {
        triggerToast("تنبيه: رقم الهوية الوطنية الموحدة يجب أن يتكون من 12 رقماً عشرياً متتالياً.");
        return;
      }
    }

    // 3. Iraqi Mobile Phone formatting validation
    const cleanPhone = (newPhone || "").trim().replace(/[\s\-\(\)\+]/g, "");
    const testPhoneClean = cleanPhone.startsWith("964") ? "0" + cleanPhone.slice(3) : cleanPhone;
    if (!/^07[5789]\d{8}$/.test(testPhoneClean)) {
      triggerToast("خطأ: يرجى إدخال رقم هاتف عراقي صحيح يتكون من 11 رقماً ويبدأ بالمفتاح المعتمد (077 للآسياسيل، 078 أو 079 لزين، 075 لكورك).");
      return;
    }

    // 4. Duplicate Check
    const isDuplicate = patients.some(p => {
      const pName = normalizeArabic(p.name || "");
      const pPhone = (p.phone || "").replace(/[\s\-\(\)\+]/g, "");
      return (pName && pName === normalizeArabic(newName)) || (pPhone && pPhone === testPhoneClean);
    });
    
    if (isDuplicate) {
      triggerToast("خطأ: هذا المريض مسجل مسبقاً في النظام. يرجى البحث عنه وحجز موعد جديد تفادياً لتكرار السجلات.");
      return;
    }

    try {
      const res = await fetch("/api/patients", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({ 
          name: newName, 
          phone: newPhone, 
          dob: newDob, 
          nationalId: newNationalId, 
          gender: newGender, 
          age: newAge ? parseInt(newAge, 10) : undefined,
          bloodType: newBloodType,
          allergies: newAllergies ? newAllergies.split(',').map(a => a.trim()).filter(a => a) : []
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setPatients([...patients, data]);

        // Reset all intake form controls to clean state
        setNewName("");
        setNewNationalId("");
        setNewPhone("");
        setNewGender("");
        setNewAge("");
        setNewBloodType("");
        setNewAllergies("");
        setIntakeTimeBlock("");
        
        triggerToast(`تم تسجيل بيانات المريض "${data.name}" بنجاح!`);
      } else {
        triggerToast(`خطأ من الخادم: ${data.error || "تعذر حفظ بيانات المريض. يرجى التأكد من صحة المدخلات."}`);
      }
    } catch (err: any) {
      console.error(err);
      triggerToast(`حدث خطأ في الاتصال بالخادم: ${err.message}`);
    }
  };

  const handlePayInstallment = async (recordId: string, installmentId: string) => {
    // Optimistically update local state & localStorage
    const updatedFinancials = financials.map(record => {
      if (record.id === recordId && record.installments) {
        return {
          ...record,
          installments: record.installments.map(inst => 
            inst.id === installmentId 
              ? { ...inst, status: "PAID" as const, paidDate: new Date().toISOString(), receivedBy: user.name } 
              : inst
          )
        };
      }
      return record;
    });

    setFinancials(updatedFinancials);
    localStorage.setItem("iq_doctor_standalone_ledger", JSON.stringify(updatedFinancials));
    window.dispatchEvent(new Event("clinical_ledger_updated"));

    // Also register an income automatically
    const targetRecord = updatedFinancials.find(f => f.id === recordId);
    const targetInstallment = targetRecord?.installments?.find(i => i.id === installmentId);

    if (targetRecord && targetInstallment) {
      try {
        await fetch("/api/finance/income", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
          body: JSON.stringify({
            amount: targetInstallment.amount,
            category: `تسديد قسط من الفاتورة (${targetRecord.category})`,
            description: `تم التسديد في قسم الاستعلامات بواسطة: ${user.name}`,
            patientId: targetRecord.patientId,
            patientName: targetRecord.patientName
          })
        });
      } catch (e) {
        console.error("Failed to post installment income to backend", e);
      }
    }

    triggerToast("تم تسجيل الدفعة بنجاح وطباعة إيصال السداد ضمناً!");
  };

  const handleBookAppointment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bookPatientId || !bookTime) return;

    try {
      const res = await fetch("/api/appointments", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          patientId: bookPatientId,
          physicianId: bookPhysician,
          dateTime: new Date(bookTime).toISOString(),
          department: bookDept,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setAppointments([...appointments, data]);
        setBookTime("");
        const pInfo = patients.find(p => p.id === bookPatientId);
        if (pInfo) {
          setPrintingWristband({
            patientName: pInfo.name,
            dob: pInfo.dob,
            dept: bookDept,
            time: new Date(bookTime || Date.now()).toLocaleString()
          });
        }
        triggerToast("Patient checked-in to nursing & medical waiting rows!");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleOnlineArrived = async (apptId: string) => {
    try {
      const nowIso = new Date().toISOString();
      const res = await fetch(`/api/appointments/${apptId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`
        },
        body: JSON.stringify({
          dateTime: nowIso,
          status: "PENDING"
        })
      });

      if (res.ok) {
        const updatedAppt = await res.json();
        setAppointments(prev => prev.map(a => a.id === apptId ? updatedAppt : a));
        triggerToast(`تم تحويل حالة الحجز إلى "تم الحضور" وإضافته في طابور العيادة للحظة الحالية.`);
      }
    } catch (err) {
      console.error("Clinical System Error: Failed to mark online reservation as arrived.", err);
    }
  };

  // Human receptionist takes over chat and posts a manual reply
  const handleSendManualReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedChat || !manualReply) return;

    try {
      const res = await fetch("/api/whatsapp/send-message", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          chatId: selectedChat.id,
          message: manualReply,
        }),
      });

      if (res.ok) {
        setManualReply("");
        await fetchChatsOnly();
        triggerToast("Response transmitted through Meta WhatsApp interface!");
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Instant simulator pushing a client-side message as a Patient into our Webhook endpoint
  
  const handleDirectBooking = async (p: Patient) => {
    if (!intakeDate || !intakeTimeBlock) {
      triggerToast("خطأ: يرجى تحديد تاريخ ووقت الموعد أولاً");
      return;
    }
    const combinedIsoTime = new Date(`${intakeDate}T${intakeTimeBlock}:00`).toISOString();
    try {
      const res = await fetch("/api/appointments", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          patientId: p.id,
          physicianId: intakePhysician,
          dateTime: combinedIsoTime,
          department: intakeDept,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setAppointments([...appointments, data]);
        setIntakeTimeBlock("");
        setReceptionistSearch(""); // Clear search
        setPrintingWristband({
          patientName: p.name,
          dob: p.dob,
          dept: intakeDept,
          time: new Date(combinedIsoTime).toLocaleString()
        });
        triggerToast("تم حجز موعد المريض بنجاح!");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSimulateWebhookInput = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch("/api/webhook/whatsapp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: testSimPhone,
          name: testSimName,
          message: testSimMessage,
        }),
      });

      if (res.ok) {
        setTestSimMessage("");
        await fetchChatsOnly();
        triggerToast("Simulated WhatsApp webhook patient trigger successfully evaluated.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSavePatientName = async (patientId: string) => {
    try {
      const res = await fetch(`/api/patients/${patientId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`
        },
        body: JSON.stringify({
          name: editPatientName,
          phone: editPatientPhone,
          nationalId: editPatientNationalId,
          gender: editPatientGender,
          dob: editPatientDob,
          bloodType: editPatientBloodType,
          allergies: editPatientAllergies ? editPatientAllergies.split(",").map(a => a.trim()).filter(Boolean) : []
        })
      });
      if (res.ok) {
        const updatedPatient = await res.json();
        setPatients(prev => prev.map(p => p.id === patientId ? updatedPatient : p));
        setEditingPatientId(null);
        triggerToast("تم تعديل معلومات المريض بنجاح.");
      }
    } catch (err) {
      console.error("Failed to update patient details", err);
    }
  };

  const clearNotificationsList = async () => {
    await fetch("/api/whatsapp/clear-notifications", { method: "POST" });
    setAlerts([]);
  };

  const triggerToast = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 4000);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-6">
      {/* Wristband Modal */}
      {printingWristband && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden">
            <div className="bg-[#0F172A] p-4 text-white flex justify-between items-center">
              <h3 className="font-bold text-sm flex items-center gap-2"><Printer className="w-4 h-4"/> Print Patient Wristband</h3>
              <button type="button" onClick={() => setPrintingWristband(null)} className="text-white hover:text-red-400">
                  <X className="w-5 h-5"/>
              </button>
            </div>
            
            <div id="wristband-print-area" className="p-6 relative group">
              {/* Clinical Verification: Native print/preview dialog shortcut overlay */}
              <button
                type="button"
                id="wristband-preview-shortcut"
                title="Print Preview (انقر للمعاينة والطباعة المباشرة)"
                onClick={() => {
                  const originalContent = document.body.innerHTML;
                  const printArea = document.getElementById("wristband-print-area")?.innerHTML;
                  if (printArea) {
                    const tempDiv = document.createElement("div");
                    tempDiv.innerHTML = printArea;
                    const btn = tempDiv.querySelector("#wristband-preview-shortcut");
                    if (btn) btn.remove();
                    
                    document.body.innerHTML = `<div style="padding: 20px; max-width: 300px;">${tempDiv.innerHTML}</div>`;
                    window.print();
                    document.body.innerHTML = originalContent;
                    window.location.reload();
                  }
                }}
                className="absolute top-2 right-2 bg-white/90 border border-slate-200 text-slate-700 hover:bg-[#0F172A] hover:text-white p-1.5 rounded-lg shadow-sm transition-all duration-300 z-20 flex items-center gap-1 group/btn cursor-pointer print:hidden"
              >
                <Printer className="w-3.5 h-3.5" />
                <span className="text-[9px] font-bold px-0.5 hidden group-hover/btn:inline transition-all duration-150">Print Preview</span>
              </button>

              <div className="border-2 border-dashed border-slate-300 p-4 rounded-lg bg-[#F8FAFC] flex items-center gap-4">
                <div className="w-16 h-16 bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                  <div className="text-[8px] text-center text-slate-400 font-bold uppercase tracking-widest">QR</div>
                </div>
                <div>
                   <p className="font-bold text-[#1E293B] text-lg uppercase leading-tight truncate">{printingWristband.patientName}</p>
                   <p className="text-[#64748B] font-mono text-xs mt-1 font-semibold">DOB: {printingWristband.dob}</p>
                   <p className="text-[#64748B] font-mono text-xs font-semibold">Dept: {printingWristband.dept}</p>
                   <p className="text-[#64748B] font-mono text-xs font-semibold">Arr: {printingWristband.time}</p>
                </div>
              </div>
            </div>

            <div className="bg-[#F8FAFC] p-4 border-t border-[#E2E8F0] flex justify-end gap-2">
                <button type="button" onClick={() => setPrintingWristband(null)} className="px-4 py-2 text-xs font-bold text-[#64748B] hover:text-[#1E293B] uppercase tracking-wider">Cancel</button>
                <button type="button" onClick={() => {
                     const originalContent = document.body.innerHTML;
                     const printArea = document.getElementById("wristband-print-area")?.innerHTML;
                     if(printArea){
                         const tempDiv = document.createElement("div");
                         tempDiv.innerHTML = printArea;
                         const btn = tempDiv.querySelector("#wristband-preview-shortcut");
                         if (btn) btn.remove();

                         document.body.innerHTML = `<div style="padding: 20px; max-width: 300px;">${tempDiv.innerHTML}</div>`;
                         window.print();
                         document.body.innerHTML = originalContent;
                         window.location.reload();
                     }
                }} className="px-4 py-2 bg-[#0F172A] hover:bg-slate-800 text-white rounded-md text-xs font-bold shadow-sm uppercase tracking-wider">
                    Confirm Print
                </button>
            </div>
          </div>
        </div>
      )}

      {/* EXPERT COMMENTARY: Small, responsive bilingual overlay to ask the receptionist optionally (without enforcement or alert blockers)
          if they wish to initiate physical sticker printouts for the newly registered clinic patient. */}
      {pendingPrintQuestion && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden border border-slate-100 p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center mx-auto mb-4">
              <Printer className="w-6 h-6 text-indigo-600 animate-pulse" />
            </div>
            <h3 className="text-sm font-bold text-slate-900 mb-2">
              طباعة ملصق موعد للمريض؟
            </h3>
            <p className="text-xs text-slate-500 mb-6 leading-relaxed">
              تم تسجيل المريض <strong>{pendingPrintQuestion.patientName}</strong> بنجاح وتوجيهه إلى الانتظار. هل ترغب بطباعة ملصق الموعد له الآن؟
            </p>
            <div className="flex justify-center gap-2.5">
              <button
                type="button"
                onClick={() => {
                  setPendingPrintQuestion(null);
                }}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-md text-xs font-bold hover:bg-slate-200 transition"
              >
                تخطي دون طباعة (Skip)
              </button>
              <button
                type="button"
                onClick={() => {
                  setPrintingWristband({
                    patientName: pendingPrintQuestion.patientName,
                    dob: pendingPrintQuestion.dob,
                    dept: pendingPrintQuestion.dept,
                    time: pendingPrintQuestion.time,
                  });
                  setPendingPrintQuestion(null);
                }}
                className="px-4 py-2 bg-[#0F172A] hover:bg-slate-800 text-white rounded-md text-xs font-bold transition"
              >
                طباعة الملصق (Print)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Alert */}
      {successMsg && (
        <div className="fixed top-6 right-6 z-50 bg-[#0F172A] text-white font-medium text-xs px-4 py-3 rounded-md shadow-md border border-[#E2E8F0] flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-[#10B981]" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Header Panel */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="bg-[#F8FAFC] text-[#64748B] font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border border-[#E2E8F0]">
              Front Reception Office
            </span>
            <span
              className={`font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border flex items-center gap-1 ${
                isOnline
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-rose-50 text-rose-700 border-rose-200 animate-pulse"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${isOnline ? "bg-emerald-500" : "bg-rose-500"}`}
              ></span>
              {isOnline ? "System Online" : "Offline - Connection Lost"}
            </span>
          </div>
          <h1 className="text-base font-bold text-[#1E293B] mt-2">Arriving Intake & Bot Command Center</h1>
          <p className="text-[#64748B] text-xs font-medium">Logged in as {user.name} • Fatema & Laila Hub node</p>
        </div>
        <button 
          onClick={fetchData} 
          className="bg-white hover:bg-[#F8FAFC] text-[#0F172A] px-3 py-2 rounded-md cursor-pointer transition select-none flex items-center gap-1.5 font-bold text-xs border border-[#E2E8F0]"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Force Sync Database
        </button>
      </div>

      {/* Summary Statistics Widget */}
      <StatsBanner apiToken={apiToken} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Step 1: Patient demographics intake creation - Expanded to xl:col-span-2 for spacious registration flow */}
        <div className="xl:col-span-2 space-y-6">
          {/* Patient Quick Search Archive & Next Visit Config */}
          <div className="bg-white border border-indigo-100 rounded-lg p-5 shadow-sm bg-gradient-to-br from-white to-indigo-50/20 space-y-4">
            <h3 className="font-bold text-indigo-950 text-xs mb-1 flex items-center gap-2 uppercase tracking-wide">
              <Users className="w-4 h-4 text-indigo-600" />
              البحث وتسجيل الزيارة بضغطة واحدة (Patient Search Desk)
            </h3>
            <p className="text-[10.5px] text-slate-500 mb-2 leading-tight">
              ابحث باسم المريض أو رقم الهاتف أو رقم البطاقة الموحدة (الهوية) لتفادي تكرار التسجيل وتحديث العمر وتسجيل الزيارة فوراً بضغطة واحدة.
            </p>

            {/* Next Appointment Settings inside the search card for direct appointment time selection */}
            <div className="bg-white p-3 rounded-lg border border-indigo-100 space-y-2 text-right">
              <span className="text-[9.5px] font-bold text-indigo-900 uppercase tracking-wider block border-b border-indigo-50 pb-1">
                ⚙️ إعدادات الزيارة القادمة (Next Appointment Target Options)
              </span>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-[#64748B] uppercase mb-1">تاريخ الموعد *</label>
                  <input
                    type="date"
                    value={intakeDate}
                    onChange={(e) => setIntakeDate(e.target.value)}
                    min={new Date().toLocaleDateString('en-CA')}
                    className="w-full bg-slate-50 border border-slate-200 text-xs text-[#1E293B] font-semibold p-1 px-2 rounded outline-none focus:border-indigo-500 text-right mb-2"
                  />
                  <label className="block text-[10px] font-bold text-[#64748B] uppercase mb-1">وقت الموعد (مواعيد مخصصة) *</label>
                  <select
                    value={intakeTimeBlock}
                    onChange={(e) => setIntakeTimeBlock(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 text-xs text-[#1E293B] font-semibold p-1 px-2 rounded outline-none focus:border-indigo-500 text-right"
                  >
                    <option value="">-- اختر وقتاً (كل 10 دقائق) --</option>
                    {(() => {
                      const slots = [];
                      for (let h = 0; h < 24; h++) {
                        if ((h >= 10 && h < 14) || (h >= 17 && h < 21)) {
                          for (let m = 0; m < 60; m += 10) {
                            slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
                          }
                        }
                      }
                      return slots.map(slot => {
                        const isTaken = appointments.some(app => {
                          if (app.status === "COMPLETED" || (app.status as any) === "CANCELLED") return false;
                          const d = new Date(app.dateTime);
                          if (d.toLocaleDateString("en-CA") !== intakeDate) return false;
                          const [sh, sm] = slot.split(":").map(Number);
                          return d.getHours() === sh && d.getMinutes() >= sm && d.getMinutes() < sm + 10;
                        });
                        return (
                          <option key={slot} value={slot} disabled={isTaken}>
                            {slot} {isTaken ? "(مسجل / محجوز)" : ""}
                          </option>
                        );
                      });
                    })()}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-[#64748B] uppercase mb-1">الأخصائي المعالج</label>
                  <select
                    value={intakePhysician}
                    onChange={(e) => setIntakePhysician(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 text-xs text-[#1E293B] font-semibold p-1 rounded outline-none focus:border-indigo-500"
                  >
                    <option value="mytradefuture@gmail.com">Dr. Watheq Al-Chalabi</option>
<option value="dr.ali@clinic.iq">دكتورة جمانة مصطفى الجلبي</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-[#64748B] uppercase mb-1">القسم الطبي</label>
                  <select
                    value={intakeDept}
                    onChange={(e) => {
                      const val = e.target.value as "GP" | "DERMATOLOGY";
                      setIntakeDept(val);
                      if (val === "GP") { setIntakePhysician("mytradefuture@gmail.com"); } else { setIntakePhysician("dr.ali@clinic.iq"); }
                    }}
                    className="w-full bg-slate-50 border border-slate-200 text-xs text-[#1E293B] font-semibold p-1 rounded outline-none focus:border-indigo-500"
                  >
                    <option value="DERMATOLOGY">Aesthetics / Derm (جلدية)</option>
                    <option value="GP">General Practice (باطنية)</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="relative">
              <input
                type="text"
                placeholder="ابحث بالاسم، رقم الهاتف، أو رقم الهوية الموحدة لتعدين سجله وحجزه بنقرة واحدة..."
                value={receptionistSearch}
                onChange={(e) => setReceptionistSearch(e.target.value)}
                className="w-full bg-white border border-indigo-200 text-xs text-[#1E293B] font-semibold px-3 py-2.5 rounded-md outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 duration-150 text-right pr-2"
              />
            </div>

            {receptionistSearch.trim() && (
              <div className="mt-4 border border-indigo-100 bg-white rounded-lg p-2 max-h-[220px] overflow-y-auto space-y-1.5 shadow-inner">
                {(() => {
                  const queryNorm = normalizeArabic(receptionistSearch);
                  const searchClean = receptionistSearch.trim().replace(/[\s\-\(\)\+]/g, "");
                  
                  const filtered = patients.filter(p => {
                    if (!p) return false;
                    const normPName = normalizeArabic(String(p.name || ""));
                    const normPPhone = String(p.phone || "").replace(/[\s\-\(\)\+]/g, "");
                    const normPNat = String(p.nationalId || "").replace(/[\s-]/g, "");
                    return (
                      (queryNorm.length > 0 && normPName.includes(queryNorm)) ||
                      (searchClean.length > 0 && normPPhone.includes(searchClean)) ||
                      (searchClean.length > 0 && normPNat.includes(searchClean))
                    );
                  });

                  if (filtered.length === 0) {
                    return (
                      <p className="text-[11px] text-slate-500 text-center py-3 font-medium">
                        لا يوجد مريض مطابق لخيارات البحث.
                      </p>
                    );
                  }

                  return filtered.map(p => {
                    const isEditingPatient = editingPatientId === p.id;
                    return (
                    <div key={p.id} className="p-2 border border-slate-50 rounded bg-slate-50/50 hover:bg-indigo-50/30 transition flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                      <div className="text-right flex-1 select-all w-full">
                        {isEditingPatient ? (
                          <div className="flex flex-col items-end gap-2 mb-2 justify-end w-full border border-indigo-100 p-3 rounded-lg bg-indigo-50/10">
                            <div className="flex items-center gap-2 justify-end w-full">
                              <span className="text-[10px] font-semibold text-slate-500">الاسم</span>
                              <input
                                type="text"
                                value={editPatientName}
                                onChange={(e) => setEditPatientName(e.target.value)}
                                className="border border-slate-300 bg-white rounded px-2 py-1 text-xs text-right outline-none w-full max-w-[200px]"
                                autoFocus
                              />
                            </div>
                            <div className="flex items-center gap-2 justify-end w-full">
                              <span className="text-[10px] font-semibold text-slate-500">الهاتف</span>
                              <input
                                type="text"
                                value={editPatientPhone}
                                onChange={(e) => setEditPatientPhone(e.target.value)}
                                className="border border-slate-300 bg-white rounded px-2 py-1 text-xs text-right outline-none w-full max-w-[200px]"
                              />
                            </div>
                            <div className="flex items-center gap-2 justify-end w-full">
                              <span className="text-[10px] font-semibold text-slate-500">رقم الهوية</span>
                              <input
                                type="text"
                                value={editPatientNationalId}
                                onChange={(e) => setEditPatientNationalId(e.target.value)}
                                className="border border-slate-300 bg-white rounded px-2 py-1 text-xs text-right outline-none w-full max-w-[200px]"
                              />
                            </div>
                            <div className="flex items-center gap-2 justify-end w-full">
                              <span className="text-[10px] font-semibold text-slate-500">الجنس</span>
                              <select
                                value={editPatientGender}
                                onChange={(e) => setEditPatientGender(e.target.value)}
                                className="border border-slate-300 bg-white rounded px-2 py-1 text-xs text-right outline-none w-full max-w-[200px]"
                              >
                                <option value="">اختر الجنس</option>
                                <option value="MALE">Male / ذكر</option>
                                <option value="FEMALE">Female / أنثى</option>
                                <option value="OTHER">Other / آخر</option>
                              </select>
                            </div>
                            <div className="flex items-center gap-2 justify-end w-full">
                              <span className="text-[10px] font-semibold text-slate-500">تاريخ الميلاد</span>
                              <input
                                type="date"
                                value={editPatientDob}
                                onChange={(e) => setEditPatientDob(e.target.value)}
                                className="border border-slate-300 bg-white rounded px-2 py-1 text-xs text-right outline-none w-full max-w-[200px]"
                              />
                            </div>
                            <div className="flex items-center gap-2 justify-end w-full">
                              <span className="text-[10px] font-semibold text-slate-500">فصيلة الدم</span>
                              <select
                                value={editPatientBloodType}
                                onChange={(e) => setEditPatientBloodType(e.target.value)}
                                className="border border-slate-300 bg-white rounded px-2 py-1 text-xs text-right outline-none w-full max-w-[200px]"
                              >
                                <option value="">غير محدد</option>
                                <option value="A+">A+</option>
                                <option value="A-">A-</option>
                                <option value="B+">B+</option>
                                <option value="B-">B-</option>
                                <option value="AB+">AB+</option>
                                <option value="AB-">AB-</option>
                                <option value="O+">O+</option>
                                <option value="O-">O-</option>
                              </select>
                            </div>
                            <div className="flex items-center gap-2 justify-end w-full">
                              <span className="text-[10px] font-semibold text-slate-500">الحساسية</span>
                              <input
                                type="text"
                                value={editPatientAllergies}
                                onChange={(e) => setEditPatientAllergies(e.target.value)}
                                placeholder="مثال: Penicillin"
                                className="border border-slate-300 bg-white rounded px-2 py-1 text-xs text-right outline-none w-full max-w-[200px]"
                              />
                            </div>
                            <div className="flex items-center gap-2 justify-end mt-1">
                              <button
                                type="button"
                                onClick={() => handleSavePatientName(p.id)}
                                className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-[10px] font-bold transition cursor-pointer"
                              >
                                حفظ التعديلات
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingPatientId(null)}
                                className="px-3 py-1 bg-slate-200 hover:bg-slate-300 text-slate-600 rounded text-[10px] font-bold transition cursor-pointer"
                              >
                                إلغاء
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="font-bold text-xs text-slate-800 flex items-center gap-2 justify-end mb-0.5">
                            <button 
                              onClick={() => {
                                setEditingPatientId(p.id);
                                setEditPatientName(p.name);
                                setEditPatientPhone(p.phone || "");
                                setEditPatientNationalId(p.nationalId || "");
                                setEditPatientGender(p.gender || "");
                                setEditPatientDob(p.dob || "");
                                setEditPatientBloodType(p.bloodType || "");
                                setEditPatientAllergies(p.allergies?.join(", ") || "");
                              }}
                              className="text-indigo-500 hover:text-indigo-700 text-[10px]"
                              title="تعديل بيانات المريض"
                            >
                              ✏️
                            </button>
                            <span>{p.name}</span>
                          </div>
                        )}
                        <span className="text-[9.5px] text-slate-500 font-mono block">
                          📞 {p.phone} | 🪪 {p.nationalId || "بلا هوية"} | 🎂 {p.dob || "بلا تاريخ ميلاد"} | ⚧️ {p.gender === "MALE" ? "ذكر" : p.gender === "FEMALE" ? "أنثى" : p.gender || "غير محدد"}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDirectBooking(p)}
                        className="w-full sm:w-auto px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10.5px] font-bold transition flex items-center justify-center gap-1 cursor-pointer select-none whitespace-nowrap"
                      >
                        💾 حفظ وتسجيل الموعد
                      </button>
                    </div>
                  );
                  });
                })()}
              </div>
            )}
          </div>

          {/* Patient registration */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm">
            <h3 className="font-bold text-[#1E293B] text-xs mb-4 flex items-center gap-2 uppercase tracking-wide text-[#64748B]">
              <UserPlus className="w-4 h-4 text-[#0F172A]" />
              1. Patient Demographics Intake
            </h3>

            {(() => {
              if (!newName.trim() && !newPhone.trim() && !newNationalId.trim()) return null;
              const normInputName = normalizeArabic(newName);
              const normInputPhone = newPhone.trim().replace(/[\s\-\(\)\+]/g, "");
              const normInputNationalId = newNationalId.trim().replace(/[\s-]/g, "");

              const matched = patients.find(p => {
                if (!p) return false;
                const normPName = normalizeArabic(p.name || "");
                const normPPhone = (p.phone || "").replace(/[\s\-\(\)\+]/g, "");
                const normPNat = (p.nationalId || "").replace(/[\s-]/g, "");

                const nameMatch = normInputName.length >= 4 && (normPName.includes(normInputName) || normInputName.includes(normPName));
                const phoneMatch = normInputPhone.length >= 7 && normPPhone.includes(normInputPhone);
                const nationalIdMatch = normInputNationalId.length >= 5 && normPNat.includes(normInputNationalId);

                return nameMatch || phoneMatch || nationalIdMatch;
              });

              if (!matched) return null;

              return (
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-amber-900 text-xs flex flex-col gap-2 mb-4 animate-fade-in text-right">
                  <div className="flex items-center gap-1.5 font-bold justify-end">
                    <span>⚠️ تنبيه: المريض مسجل مسبقاً!</span>
                  </div>
                  <p className="text-[10.5px] text-amber-800 leading-normal">
                    تم العثور على مريض مطابق باسم <strong>{matched.name}</strong> وهاتف {matched.phone}. يمكنك إدراج بيانات المريض في النموذج بضغطة واحدة وتحديث العمر.
                  </p>
                  <button
                    type="button"
                    onClick={() => handleOneClickCheckIn(matched)}
                    className="w-full py-1.5 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded text-[10.5px] cursor-pointer transition flex items-center justify-center gap-1 select-none"
                  >
                    ⚡ إدراج بيانات المريض وتحديث العمر لتسجيل زيارته
                  </button>
                </div>
              );
            })()}

            <form onSubmit={handleRegisterPatient} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-right">
                <div className="md:col-span-2 lg:col-span-3 text-right">
                  <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1">Patient Full Name (الاسم الثلاثي الصحيح)</label>
                  <input
                    type="text"
                    placeholder="الاسم الأول + الأب + الجد (مثال: أحمد عباس السوداني)"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="w-full bg-white border border-[#E2E8F0] text-xs text-[#1E293B] font-semibold px-3 py-2 rounded-md outline-none focus:border-[#0F172A] duration-150 text-right"
                    required
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1 text-right">Iraqi National Unified ID (البطاقة الموحدة)</label>
                  <input
                    type="text"
                    placeholder="مثال: 198012345678"
                    value={newNationalId}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^\d]/g, ""); // Permit digits only
                      setNewNationalId(val.slice(0, 12));
                    }}
                    className="w-full bg-white border border-[#E2E8F0] text-xs text-[#1E293B] font-mono font-semibold px-3 py-2 rounded-md outline-none focus:border-[#0F172A] duration-150 text-center"
                    maxLength={12}
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1 text-right">Iraqi Phone Number (رقم الهاتف)</label>
                  <input
                    type="text"
                    placeholder="مثال: 07XXXXXXXX"
                    value={newPhone}
                    onChange={(e) => {
                      setNewPhone(e.target.value);
                    }}
                    className="w-full bg-white border border-[#E2E8F0] text-xs text-[#1E293B] font-semibold px-3 py-2 rounded-md outline-none font-mono focus:border-[#0F172A] duration-150 text-left"
                    required
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1 text-right">Date of Birth (DOB)</label>
                  <input
                    type="date"
                    value={newDob}
                    onChange={(e) => {
                      const dobVal = e.target.value;
                      setNewDob(dobVal);
                      try {
                        /* Clinical Security & Validation: Check clinical validity of entered date string to ensure sound calculations */
                        if (dobVal) {
                          const birthDate = new Date(dobVal);
                          if (!isNaN(birthDate.getTime())) {
                            const today = new Date();
                            let calculatedAge = today.getFullYear() - birthDate.getFullYear();
                            const monthDiff = today.getMonth() - birthDate.getMonth();
                            
                            // Refine age calculation if birthday hasn't occurred yet in the current year
                            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                              calculatedAge--;
                            }
                            
                            /* Expert Commentary: Seamlessly sync computed biological age back into registration record */
                            setNewAge(Math.max(0, calculatedAge).toString());
                          }
                        }
                      } catch (error) {
                        /* Robust Error Handling: Safe error logging to preserve clinic interface reliability */
                        console.error("Clinical System Alert: Failed to auto-calculate raw birth age metric.", error);
                      }
                    }}
                    className="w-full bg-white border border-[#E2E8F0] text-xs text-[#1E293B] font-semibold px-3 py-2 rounded-md outline-none focus:border-[#0F172A] duration-150"
                    required
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1 text-right">Gender</label>
                  <select
                    value={newGender}
                    onChange={(e) => setNewGender(e.target.value)}
                    className="w-full bg-white border border-[#E2E8F0] text-xs text-[#1E293B] font-semibold px-3 py-2 rounded-md outline-none focus:border-[#0F172A] duration-150"
                  >
                    <option value="">Select Gender</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1 text-right">Age</label>
                  <input
                    type="number"
                    placeholder="e.g. 29"
                    value={newAge}
                    onChange={(e) => setNewAge(e.target.value)}
                    className="w-full bg-white border border-[#E2E8F0] text-xs text-[#1E293B] font-semibold px-3 py-2 rounded-md outline-none focus:border-[#0F172A] duration-150"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1 text-right">Blood Type</label>
                  <select
                    value={newBloodType}
                    onChange={(e) => setNewBloodType(e.target.value)}
                    className="w-full bg-white border border-[#E2E8F0] text-xs text-[#1E293B] font-semibold px-3 py-2 rounded-md outline-none focus:border-[#0F172A] duration-150 animate-fade-in"
                  >
                    <option value="">Unknown</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>

                <div className="md:col-span-2 lg:col-span-3 text-right">
                  <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1">Allergies (الحساسية - مفصولة بفواصل)</label>
                  <input
                    type="text"
                    placeholder="e.g. Penicillin, Peanuts"
                    value={newAllergies}
                    onChange={(e) => setNewAllergies(e.target.value)}
                    className="w-full bg-white border border-[#E2E8F0] text-xs text-[#1E293B] font-semibold px-3 py-2 rounded-md outline-none focus:border-[#0F172A] duration-150 text-right"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full h-11 bg-[#0F172A] hover:bg-slate-800 text-white font-bold text-xs rounded-md cursor-pointer transition uppercase tracking-wider text-center"
              >
                Save Patient Only (تسجيل بيانات المريض فقط)
              </button>
            </form>
          </div>

          <GoogleCalendarSync appointments={appointments} patients={patients} apiToken={apiToken} />
        </div>

        {/* Step 2: Live WhatsApp auto-triage and takeover (Reduced column layout to fit screen space optimally) */}
        <div className="xl:col-span-1 space-y-6">
          <div className="space-y-6">
            {/* Left WhatsApp sub-panel: Chats list */}
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 shadow-sm">
              <h3 className="font-bold text-[#1E293B] text-xs mb-4 flex items-center justify-between">
                <span className="flex items-center gap-1.5 uppercase tracking-wide text-[#64748B]">
                  <Phone className="w-4 h-4 text-[#0F172A]" />
                  State-Machine Queue
                </span>
                <span className="text-[8px] bg-[#F8FAFC] text-[#0F172A] font-bold px-2 py-0.5 rounded border border-[#E2E8F0] uppercase tracking-wider">
                  State logic
                </span>
              </h3>

              {/* Push alert lists */}
              {alerts.length > 0 && (
                <div className="mb-4 bg-[#FFFBEB] border border-[#FDE68A] rounded-md p-3 text-[10px] text-[#78350F] shadow-sm relative">
                  <h4 className="font-bold flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5 text-[#D97706]" />
                    Human takeover needed!
                  </h4>
                  <p className="mt-1 font-medium">{alerts[alerts.length - 1]}</p>
                  <button
                    onClick={clearNotificationsList}
                    className="mt-2 text-[#B45309] hover:underline font-bold text-[9px] cursor-pointer"
                  >
                    Ack Alert Clearance
                  </button>
                </div>
              )}

              <div className="space-y-1.5 max-h-[350px] overflow-y-auto pr-1">
                {[...whatsappChats].sort((a, b) => {
                  if (a.isHighPriority && !b.isHighPriority) return -1;
                  if (!a.isHighPriority && b.isHighPriority) return 1;
                  return new Date(b.lastActive).getTime() - new Date(a.lastActive).getTime();
                }).map((chat) => {
                  const isSelected = selectedChat?.id === chat.id;
                  const waitingForHuman = chat.state === "HUMAN_TAKEOVER";

                  return (
                    <div
                      key={chat.id}
                      onClick={() => setSelectedChat(chat)}
                      className={`p-2.5 rounded-md border cursor-pointer select-none transition flex flex-col gap-1 ${
                        isSelected
                          ? "bg-[#F8FAFC] border-[#0F172A] shadow-sm"
                          : chat.isHighPriority
                          ? "bg-red-50 border-red-200 hover:bg-red-100"
                          : "bg-white border-[#E2E8F0] hover:bg-[#F8FAFC]"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <strong className="text-[#1E293B] text-xs block truncate max-w-[120px] font-semibold">{chat.name}</strong>
                        {chat.isHighPriority ? (
                          <span className="bg-red-600 border border-red-700 text-white px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider animate-pulse">
                            HIGH PRIORITY
                          </span>
                        ) : waitingForHuman ? (
                          <span className="bg-rose-50 border border-rose-200 text-rose-700 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider">
                            TAKEOVER REQ
                          </span>
                        ) : (
                          <span className="bg-[#F8FAFC] text-[#64748B] border border-[#E2E8F0] px-1 rounded text-[8px] font-mono uppercase tracking-wider font-semibold">
                            {chat.state}
                          </span>
                        )}
                      </div>
                      <p className="text-[9px] text-[#64748B] font-mono mt-0.5">{chat.phone}</p>
                      {chat.messages.length > 0 && (
                        <p className={`text-[10px] line-clamp-1 italic text-ellipsis overflow-hidden font-medium ${chat.isHighPriority ? 'text-red-700' : 'text-[#64748B]'}`}>
                          "{chat.messages[chat.messages.length - 1].text}"
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right WhatsApp sub-panel: Chat dialogue stream */}
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 shadow-sm flex flex-col justify-between h-[450px]">
              {selectedChat ? (
                <>
                  <div className="flex border-b border-[#E2E8F0] pb-3 justify-between items-center bg-white">
                    <div>
                      <h4 className="font-bold text-[#1E293B] text-xs">{selectedChat.name}</h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-[9px] text-[#64748B] font-mono tracking-wide font-medium">Triage state: {selectedChat.state}</p>
                        <span className="text-[8px] text-gray-300">|</span>
                        <a
                          href={`https://wa.me/${selectedChat.phone.replace(/\D/g, "")}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[9px] text-green-600 hover:text-green-700 hover:underline font-bold flex items-center gap-1"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                          افتح في واتس اب الحقيقي
                        </a>
                      </div>
                    </div>
                    {selectedChat.language && (
                      <span className="text-[8px] bg-[#F8FAFC] text-[#0F172A] border border-[#E2E8F0] px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                        LANG: {selectedChat.language === "ar" ? "العربية" : "ENGLISH"}
                      </span>
                    )}
                  </div>

                  {/* Dialogue thread scroller */}
                  <div className="flex-1 overflow-y-auto my-4 space-y-2 pr-1 font-sans text-xs max-h-[290px]">
                    {selectedChat.messages.map((m, idx) => (
                      <div
                        key={idx}
                        className={`max-w-[80%] rounded-md p-2.5 leading-relaxed ${
                          m.sender === "patient"
                            ? "bg-[#F8FAFC] text-[#1E293B] mr-auto text-left border border-[#E2E8F0] font-medium"
                            : m.sender === "bot"
                            ? "bg-[#F8FAFC] text-[#64748B] ml-auto text-left border border-[#E2E8F0] italic font-semibold"
                            : "bg-[#0F172A] text-white ml-auto text-right font-medium"
                        }`}
                      >
                        <p className="whitespace-pre-line text-[10.5px]">{m.text}</p>
                        <span className="text-[8px] text-[#64748B] opacity-90 block text-right mt-1 font-mono">
                          {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Reply Input Form */}
                  <form onSubmit={handleSendManualReply} className="flex gap-2">
                    <input
                      type="text"
                      className="flex-1 bg-white border border-[#E2E8F0] px-3 py-2 rounded-md outline-none text-[#1E293B] text-xs font-semibold focus:border-[#0F172A] duration-155"
                      placeholder="Type diagnostic followup message..."
                      value={manualReply}
                      onChange={(e) => setManualReply(e.target.value)}
                      required
                    />
                    <button
                      type="submit"
                      className="bg-[#0F172A] hover:bg-slate-800 p-2 text-white rounded-md shadow cursor-pointer transition select-none"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </form>
                </>
              ) : (
                <div className="h-full flex flex-col justify-center items-center text-center p-8 text-slate-300">
                  <MessageSquare className="w-8 h-8 mb-2 text-[#94A3B8]" />
                  <h4 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider text-[#64748B]">Awaiting Chat Selection</h4>
                  <p className="text-[11px] text-[#64748B] mt-1 max-w-[190px] font-medium">
                    Click on a phone session queue items to monitor live triage outputs and send custom client texts.
                  </p>
                </div>
              )}
            </div>
          </div>


        </div>
      </div>

      {(() => {
        try {
          const now = new Date();
          // Filter future appointments only to satisfy the user criteria
          const futureAppts = appointments.filter(a => new Date(a.dateTime) > now);

          // Extract unique dates of future bookings
          const futureDates = Array.from(new Set(futureAppts.map(a => {
            const d = new Date(a.dateTime);
            return d.toLocaleDateString("en-CA"); // YYYY-MM-DD
          }))).sort();

          // Default to first future date if none chosen and there is one
          const activeDiaryDate = selectedDiaryDate || (futureDates.length > 0 ? futureDates[0] : "");

          // Generate 10-minute slots strictly during clinic working hours:
          // 10:00 AM to 2:00 PM (10-14) & 5:00 PM to 9:00 PM (17-21)
          const timeSlots: string[] = [];
          for (let h = 0; h < 24; h++) {
            if ((h >= 10 && h < 14) || (h >= 17 && h < 21)) {
              for (let m = 0; m < 60; m += 10) {
                timeSlots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
              }
            }
          }

          return (
            <div id="clinic-diary-container" className="mt-8 bg-white border border-indigo-100 rounded-lg p-5 shadow-sm">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4 border-b border-indigo-50 pb-3">
                <div className="text-right flex-1 sm:text-left sm:flex-initial">
                  <span id="clinic-diary-badge" className="bg-indigo-50 border border-indigo-100 text-indigo-700 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
                    📆 دفتر مواعيد الـ 10 دقائق / 10-Minute Appointment Diary
                  </span>
                  <h2 id="clinic-diary-title" className="text-xs font-bold text-slate-800 mt-2 uppercase tracking-wide">
                    Future Registrations Only Schedule / جدول المواعيد المستقبلية فقط
                  </h2>
                  <p id="clinic-diary-subtitle" className="text-[10.5px] text-slate-500 mt-0.5 leading-relaxed">
                    يعرض هذا الدفتر المواعيد المسجلة مستقبلياً والمجدولة بدقة عالية في فترات زمنية بطول ١٠ دقائق لتمكين منسق الاستقبال من تتبع حجوزات العيادة.
                  </p>
                </div>
                
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-600 font-bold cursor-pointer select-none">
                    <input
                      id="diary-empty-toggle"
                      type="checkbox"
                      checked={showEmptyDiarySlots}
                      onChange={(e) => setShowEmptyDiarySlots(e.target.checked)}
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                    />
                    <span>عرض الفترات الشاغرة (Show empty blocks)</span>
                  </label>
                </div>
              </div>

              {/* Selected Date Navigation Tabs */}
              {futureDates.length > 0 ? (
                <div id="diary-date-nav" className="flex flex-wrap gap-1.5 mb-4 p-2 bg-slate-50 border border-slate-100 rounded-lg">
                  {futureDates.map(dStr => {
                    const isSelected = activeDiaryDate === dStr;
                    const apptCount = futureAppts.filter(a => {
                      const apptD = new Date(a.dateTime);
                      return apptD.toLocaleDateString("en-CA") === dStr;
                    }).length;

                    const formattedLabel = new Date(dStr).toLocaleDateString("ar-IQ", {
                      weekday: "long",
                      day: "numeric",
                      month: "short"
                    });

                    return (
                      <button
                        key={dStr}
                        id={`diary-date-btn-${dStr}`}
                        type="button"
                        onClick={() => setSelectedDiaryDate(dStr)}
                        className={`px-3 py-1.5 text-xs font-bold rounded transition cursor-pointer flex items-center gap-1.5 border ${
                          isSelected
                            ? "bg-indigo-600 text-white border-indigo-700 shadow-sm"
                            : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{formattedLabel} ({dStr})</span>
                        <span className={`text-[9px] px-1.5 py-0.2 rounded-full ${isSelected ? "bg-white/20 text-white" : "bg-indigo-50 text-indigo-700 border border-indigo-100 font-bold"}`}>
                          {apptCount}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div id="clinic-diary-empty" className="text-center py-8 border border-dashed border-slate-200 rounded-md bg-slate-50/10 mb-2">
                  <Calendar className="w-8 h-8 text-slate-300 mx-auto mb-2 animate-bounce" />
                  <h4 className="font-bold text-slate-700 text-xs uppercase tracking-wider">No Future Registrations Logged / لا توجد حجوزات مستقبلية مسجلة</h4>
                  <p className="text-slate-400 text-[10.5px] mt-1">
                    جميع الحجوزات والمواعيد الحالية تقع في الماضي أو اليوم الحالي كزيارات دخول فورية.
                  </p>
                </div>
              )}

              {activeDiaryDate && (
                <div id="clinic-diary-slots-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {(() => {
                    const rendered = timeSlots.map(slot => {
                      const apptsInSlot = futureAppts.filter(a => {
                        const apptDate = new Date(a.dateTime);
                        const isSameDate = apptDate.toLocaleDateString("en-CA") === activeDiaryDate;
                        if (!isSameDate) return false;
                        
                        const apptHour = apptDate.getHours();
                        const apptMin = apptDate.getMinutes();
                        const [slotHour, slotMin] = slot.split(":").map(Number);
                        
                        return apptHour === slotHour && apptMin >= slotMin && apptMin < slotMin + 10;
                      });

                      const isOccupied = apptsInSlot.length > 0;

                      // Skip empty slots if the toggle to show empty ones is falsy
                      if (!isOccupied && !showEmptyDiarySlots) return null;

                      return { slot, isOccupied, apptsInSlot };
                    }).filter((item): item is { slot: string; isOccupied: boolean; apptsInSlot: Appointment[] } => item !== null);

                    if (rendered.length === 0) {
                      return (
                        <div className="col-span-full py-6 text-center text-slate-500 font-semibold text-xs bg-slate-50 border rounded-lg border-dashed">
                           لا توجد حجوزات مستقبلية مدرجة لفترات الـ 10 دقائق في تاريخ {activeDiaryDate}. يمكنك حجز موعد جديد في المستقبل لإدراجه هنا.
                        </div>
                      );
                    }

                    return rendered.map(item => {
                      const { slot, isOccupied, apptsInSlot } = item;
                      const [hStr, mStr] = slot.split(":");
                      const hourNum = Number(hStr);
                      const ampm = hourNum >= 12 ? "مساءً" : "صباحاً";
                      const displayHour = hourNum % 12 || 12;
                      const timeLabel = `${displayHour}:${mStr} ${ampm}`;

                      return (
                        <div
                          key={slot}
                          id={`diary-slot-card-${slot}`}
                          className={`p-3 rounded-lg border transition duration-150 ${
                            isOccupied
                              ? "bg-indigo-50 border-indigo-200 shadow-sm"
                              : "bg-slate-50/50 border-slate-200 opacity-65 hover:opacity-100 hover:border-indigo-400 hover:shadow-md cursor-pointer"
                          }`}
                          onClick={() => {
                            if (!isOccupied) {
                              setIntakeDate(activeDiaryDate);
                              setIntakeTimeBlock(slot);
                              const el = document.getElementById("booking-section");
                              if (el) {
                                el.scrollIntoView({ behavior: "smooth" });
                              }
                              triggerToast(`تم تحديد الموعد المتاح: ${activeDiaryDate} الساعة ${timeLabel}`);
                            }
                          }}
                        >
                          <div className="flex justify-between items-center mb-2">
                            <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded border ${
                              isOccupied ? "bg-indigo-600 text-white border-indigo-700" : "bg-slate-200 text-slate-700 border-slate-300"
                            }`}>
                              {slot} | {timeLabel}
                            </span>
                            {isOccupied ? (
                              <span className="text-[9px] font-bold text-indigo-700 bg-indigo-100 border border-indigo-200 px-1.5 py-0.5 rounded">
                                محجوز
                              </span>
                            ) : (
                              <span className="text-[9px] font-bold text-slate-400">
                                متاح
                              </span>
                            )}
                          </div>

                          {isOccupied ? (
                            <div className="space-y-2 mt-2">
                              {apptsInSlot.map(appt => {
                                const patient = patients.find(p => p.id === appt.patientId);
                                if (!patient) return null;

                                const isEditing = editingApptId === appt.id;
                                
                                if (isEditing) {
                                  return (
                                    <div key={appt.id} className="p-2 bg-amber-50 rounded border border-amber-200 space-y-2" dir="rtl">
                                      <div className="text-right">
                                        <span className="font-bold text-xs text-slate-900 block truncate">{patient.name}</span>
                                        <span className="text-[10px] text-slate-500 font-mono block">📞 {patient.phone}</span>
                                      </div>
                                      
                                      <div className="grid grid-cols-2 gap-2 text-xs">
                                        <select
                                          value={editPhysician}
                                          onChange={(e) => setEditPhysician(e.target.value)}
                                          className="bg-white border border-slate-300 text-[10px] px-1 py-1 rounded w-full outline-none"
                                        >
                                          <option value="mytradefuture@gmail.com">Dr. Watheq Al-Chalabi</option>
                                          <option value="dr.ali@clinic.iq">دكتورة جمانة مصطفى الجلبي</option>
                                        </select>
                                        
                                        <select
                                          value={editDept}
                                          onChange={(e) => setEditDept(e.target.value as any)}
                                          className="bg-white border border-slate-300 text-[10px] px-1 py-1 rounded w-full outline-none"
                                        >
                                          <option value="DERMATOLOGY">DERMATOLOGY</option>
                                          <option value="GP">GP</option>
                                        </select>
                                        
                                        <input
                                          type="datetime-local"
                                          value={editTime}
                                          onChange={(e) => setEditTime(e.target.value)}
                                          className="col-span-2 bg-white border border-slate-300 text-[10px] font-mono p-1 rounded w-full outline-none"
                                        />
                                        
                                        <select
                                          value={editStatus}
                                          onChange={(e) => setEditStatus(e.target.value as any)}
                                          className="col-span-2 bg-white border border-slate-300 text-[10px] font-bold px-1 py-1 rounded w-full outline-none"
                                        >
                                          <option value="PENDING">في الانتظار (WAITING / PENDING)</option>
                                          <option value="IN_PROGRESS">قيد الإجراء / معلق (IN_PROGRESS)</option>
                                          <option value="COMPLETED">مكتمل (COMPLETED)</option>
                                          <option value="MISSED">فائت (MISSED)</option>
                                        </select>
                                      </div>
                                      
                                      <div className="flex gap-1.5 justify-end pt-1">
                                        <button
                                          type="button"
                                          onClick={() => setEditingApptId(null)}
                                          className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold text-[9px] rounded px-2 py-1 cursor-pointer transition select-none"
                                        >
                                          إلغاء
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleUpdateAppointment(appt.id)}
                                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[9px] rounded px-3 py-1 cursor-pointer transition select-none"
                                        >
                                          حفظ
                                        </button>
                                      </div>
                                    </div>
                                  );
                                }

                                return (
                                  <div key={appt.id} id={`diary-patient-badge-${appt.id}`} className="p-2 bg-white rounded border border-indigo-100 space-y-1.5">
                                    <div className="text-right">
                                      <div className="flex items-center justify-between gap-1">
                                        <span className="font-bold text-xs text-slate-800 block truncate">{patient.name}</span>
                                        <button
                                          type="button"
                                          onClick={() => handleSendTelegram(patient.id)}
                                          disabled={tgStatus[patient.id] === 'loading'}
                                          className={`p-0.5 rounded transition shrink-0 inline-flex items-center justify-center ${
                                            tgStatus[patient.id] === 'loading' ? 'animate-pulse text-slate-400 bg-slate-50' :
                                            tgStatus[patient.id] === 'success' ? 'text-emerald-600 bg-emerald-50' :
                                            tgStatus[patient.id] === 'error' ? 'text-rose-600 bg-rose-50' :
                                            'text-sky-500 hover:text-sky-600 hover:bg-sky-50'
                                          }`}
                                          title="إرسال إشعار تليجرام / Send Telegram alert"
                                        >
                                          {tgStatus[patient.id] === 'loading' ? (
                                            <span className="text-[10px]">⏳</span>
                                          ) : tgStatus[patient.id] === 'success' ? (
                                            <span className="text-[10px]">✅</span>
                                          ) : tgStatus[patient.id] === 'error' ? (
                                            <span className="text-[10px]">⚠️</span>
                                          ) : (
                                            <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.02-1.96 1.24-5.54 3.65-.52.36-.99.53-1.41.52-.46-.01-1.35-.26-2.01-.48-.81-.27-1.46-.42-1.4-.88.03-.24.37-.48 1.02-.73 3.98-1.73 6.64-2.87 7.99-3.41 3.8-1.55 4.59-1.82 5.1-.1.11.24.18.52.2.82z"/>
                                            </svg>
                                          )}
                                        </button>
                                      </div>
                                      <span className="text-[10px] text-slate-500 font-mono block">📞 {patient.phone}</span>
                                    </div>

                                    <div className="flex flex-wrap gap-1 mt-1 justify-end">
                                      <span className="text-[8px] bg-indigo-50 border border-indigo-100 text-indigo-700 rounded px-1.5 py-0.2 font-bold font-mono">
                                        {appt.department}
                                      </span>
                                      <span className="text-[8px] bg-slate-100 border border-slate-200 text-slate-700 rounded px-1.5 py-0.2 font-bold max-w-[120px] truncate">
                                        {(appt.physicianId || "").toLowerCase().includes("ali") ? "د. جمانة الجلبي" :
                                          "د. واثق"}
                                      </span>
                                    </div>

                                    <div className="border-t border-slate-100 pt-1.5 flex justify-between items-center gap-1 text-[10px]">
                                      <div className="flex gap-1">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setEditingApptId(appt.id);
                                            const localDT = new Date(new Date(appt.dateTime).getTime() - new Date(appt.dateTime).getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                                            setEditTime(localDT);
                                            setEditStatus(appt.status as any);
                                            setEditPhysician(appt.physicianId || "");
                                            setEditDept(appt.department as any);
                                          }}
                                          className="text-indigo-600 hover:text-white hover:bg-indigo-600 border border-indigo-600 font-bold inline-flex items-center px-1 py-0.5 rounded text-[8.5px] cursor-pointer transition"
                                        >
                                          <span>تعديل</span>
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteAppointment(appt.id)}
                                          className="text-rose-600 hover:text-white hover:bg-rose-600 border border-rose-600 font-bold inline-flex items-center px-1 py-0.5 rounded text-[8.5px] cursor-pointer transition"
                                        >
                                          <span>حذف</span>
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setPrintingWristband({
                                              patientName: patient.name,
                                              dob: patient.dob,
                                              dept: appt.department,
                                              time: new Date(appt.dateTime).toLocaleString()
                                            });
                                          }}
                                          className="text-slate-500 hover:text-indigo-600 font-bold inline-flex items-center px-1 py-0.5 text-[10px] cursor-pointer"
                                        >
                                          <Printer className="w-3.5 h-3.5" />
                                        </button>
                                        <div className="scale-75 origin-right">
                                          <SendWhatsAppButton
                                            patientPhone={patient.phone}
                                            messageContent={`🩺 *تأكيد حجز الدكتور واثق الجلبي (IQ-Doctor)*\n\n👤 *المريض:* ${patient.name}\n📅 *موعد الحجز:* ${new Date(appt.dateTime).toLocaleString("ar-IQ", { day: "numeric", month: "numeric", hour: "numeric", minute: "2-digit" })}\n🩺 *العيادة:* ${appt.department === "DERMATOLOGY" ? "الجلدية والليزر" : "الطب العام والباطني"}\n👨‍⚕️ *الطبيب:* ${(appt.physicianId || "").toLowerCase().includes("ali") ? "د. جمانة مصطفى الجلبي" : "Dr. Watheq Al-Chalabi"}\n\n📍 العراق - بغداد - السيدية\n📞 للاستفسار وتأكيد الحضور يرجى التواصل معنا.\n🔒 نظام لإدارة العيادات الطبية الآمن`}
                                            label="Booking"
                                          />
                                        </div>
                                      </div>
                                      <span className="text-[9px] font-bold text-[#64748B] bg-slate-100 border border-slate-200 rounded px-1">{appt.status}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-center py-2 text-slate-400 text-[10px] italic">
                              فترة شاغرة
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          );
        } catch (err) {
          console.error("Clinical System Error: Failed to render the 10-minute clinic diary section.", err);
          return (
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg text-red-800 text-xs">
              فشل تحميل جدول مواعيد الاستقبال (١٠ دقائق).
            </div>
          );
        }
      })()}

      {/* EXPERT COMMENTARY: Live Patient State Tracking and Active Clinic Queue list (لمعرفة حالة المريض وتتبع الانتظار أو اكتمال الزيارة) */}
      <div id="active-queue-section" className="mt-8 bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 border-b border-slate-100 pb-3">
          <div>
            <span id="active-queue-badge" className="bg-[#EEF2F6] text-[#0F172A] font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border border-slate-200">
              Live Patient State Tracking / متابعة حالة المرضى بالعيادة
            </span>
            <h2 id="active-queue-title" className="text-xs font-bold text-slate-800 mt-1 uppercase tracking-wide">
              Active Hospital Clinic Waiting Queue & Patient Status / قائمة الانتظار الحالية وحالة المرضى
            </h2>
          </div>
          <div id="active-queue-count" className="text-[10px] text-slate-500 font-bold bg-slate-50 px-2.5 py-1 rounded border border-slate-100">
            Total active checked-in patients: {appointments.filter(a => a.status === "PENDING" || a.status === "IN_PROGRESS").length}
          </div>
        </div>

        {appointments.length > 0 ? (
          <div className="overflow-x-auto">
            <table id="active-queue-table" className="w-full text-left font-sans text-xs">
              <thead>
                <tr className="border-b border-slate-100 text-slate-400 font-bold uppercase text-[9px] tracking-wider bg-slate-50/50">
                  <th className="py-2.5 px-3">Patient Name / اسم المريض</th>
                  <th className="py-2.5 px-3">Phone / الهاتف</th>
                  <th className="py-2.5 px-3">Physician / الطبيب المعالج</th>
                  <th className="py-2.5 px-3">Department / القسم</th>
                  <th className="py-2.5 px-3">Check-In Time / وقت الدخول</th>
                  <th className="py-2.5 px-3 text-center">Status / الحالة</th>
                  <th className="py-2.5 px-3 text-right">Actions / إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                {[...appointments]
                  .filter(a => new Date(a.dateTime).toLocaleDateString("en-CA") === new Date().toLocaleDateString("en-CA"))
                  .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime())
                  .map((appt) => {
                    const patient = patients.find(p => p.id === appt.patientId);
                    if (!patient) return null;

                    const isEditing = editingApptId === appt.id;

                    if (isEditing) {
                      return (
                        <tr key={appt.id} className="bg-amber-50/75 hover:bg-amber-50 transition duration-150">
                          <td className="py-3 px-3 font-bold text-slate-900 border-b border-amber-100">{patient.name}</td>
                          <td className="py-3 px-3 font-mono text-slate-500 text-[11px] border-b border-amber-100">{patient.phone}</td>
                          
                          <td className="py-3 px-3 border-b border-amber-100">
                            <select
                              value={editPhysician}
                              onChange={(e) => setEditPhysician(e.target.value)}
                              className="bg-white border border-slate-300 text-xs px-2 py-1 rounded w-full outline-none focus:ring-1 focus:ring-indigo-505"
                            >
                              <option value="mytradefuture@gmail.com">Dr. Watheq Al-Chalabi</option>
<option value="dr.ali@clinic.iq">دكتورة جمانة مصطفى الجلبي</option>
                            </select>
                          </td>
                          
                          <td className="py-3 px-3 border-b border-amber-100">
                            <select
                              value={editDept}
                              onChange={(e) => setEditDept(e.target.value as any)}
                              className="bg-white border border-slate-300 text-xs px-2 py-1 rounded w-full outline-none focus:ring-1 focus:ring-indigo-505"
                            >
                              <option value="DERMATOLOGY">DERMATOLOGY</option>
                              <option value="GP">GP</option>
                            </select>
                          </td>

                          <td className="py-3 px-3 border-b border-amber-100">
                            <input
                              type="datetime-local"
                              value={editTime}
                              onChange={(e) => setEditTime(e.target.value)}
                              className="bg-white border border-slate-300 text-[11px] font-mono p-1 px-1.5 rounded w-full outline-none focus:ring-1 focus:ring-indigo-505"
                            />
                          </td>

                          <td className="py-3 px-3 text-center border-b border-amber-100">
                            <select
                              value={editStatus}
                              onChange={(e) => setEditStatus(e.target.value as any)}
                              className="bg-white border border-slate-300 text-[11px] font-bold px-2 py-1 rounded w-full outline-none focus:ring-1 focus:ring-indigo-505"
                            >
                              <option value="PENDING">في الانتظار (WAITING / PENDING)</option>
                              <option value="IN_PROGRESS">قيد الإجراء / معلق (IN_PROGRESS)</option>
                              <option value="COMPLETED">مكتمل (COMPLETED)</option>
                              <option value="MISSED">فائت (MISSED)</option>
                            </select>
                          </td>

                          <td className="py-3 px-3 text-right border-b border-amber-100">
                            <div className="flex gap-1.5 justify-end">
                              <button
                                type="button"
                                onClick={() => handleUpdateAppointment(appt.id)}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[10px] rounded px-3 py-1 cursor-pointer transition select-none"
                              >
                                حفظ
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingApptId(null)}
                                className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold text-[10px] rounded px-2 py-1 cursor-pointer transition select-none"
                              >
                                إلغاء
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    const isEditingPatient = editingPatientId === patient.id;

                    return (
                      <tr key={appt.id} className="hover:bg-slate-50/45 transition duration-150">
                        <td className="py-3 px-3 text-slate-900" colSpan={isEditingPatient ? 2 : 1}>
                          {isEditingPatient ? (
                            <div className="flex flex-col gap-1.5 p-2 bg-slate-50 border border-indigo-150 rounded-lg text-xs max-w-sm">
                              <div className="flex items-center gap-1.5 justify-end">
                                <span className="text-[10px] text-slate-400 font-semibold">الاسم</span>
                                <input
                                  type="text"
                                  value={editPatientName}
                                  onChange={(e) => setEditPatientName(e.target.value)}
                                  className="border border-slate-300 bg-white rounded px-2 py-0.5 text-xs text-right outline-none w-full max-w-[150px]"
                                  autoFocus
                                />
                              </div>
                              <div className="flex items-center gap-1.5 justify-end">
                                <span className="text-[10px] text-slate-400 font-semibold">الهاتف</span>
                                <input
                                  type="text"
                                  value={editPatientPhone}
                                  onChange={(e) => setEditPatientPhone(e.target.value)}
                                  className="border border-slate-300 bg-white rounded px-2 py-0.5 text-xs text-right outline-none w-full max-w-[150px]"
                                />
                              </div>
                              <div className="flex items-center gap-1.5 justify-end">
                                <span className="text-[10px] text-slate-400 font-semibold">الهوية</span>
                                <input
                                  type="text"
                                  value={editPatientNationalId}
                                  onChange={(e) => setEditPatientNationalId(e.target.value)}
                                  className="border border-slate-300 bg-white rounded px-2 py-0.5 text-xs text-right outline-none w-full max-w-[150px]"
                                />
                              </div>
                              <div className="flex items-center gap-1.5 justify-end">
                                <span className="text-[10px] text-slate-400 font-semibold">الجنس</span>
                                <select
                                  value={editPatientGender}
                                  onChange={(e) => setEditPatientGender(e.target.value)}
                                  className="border border-slate-300 bg-white rounded px-2 py-0.5 text-xs text-right outline-none w-full max-w-[150px]"
                                >
                                  <option value="">الجنس</option>
                                  <option value="MALE">Male / ذكر</option>
                                  <option value="FEMALE">Female / أنثى</option>
                                  <option value="OTHER">Other / آخر</option>
                                </select>
                              </div>
                              <div className="flex items-center gap-1.5 justify-end">
                                <span className="text-[10px] text-slate-400 font-semibold">الميلاد</span>
                                <input
                                  type="date"
                                  value={editPatientDob}
                                  onChange={(e) => setEditPatientDob(e.target.value)}
                                  className="border border-slate-300 bg-white rounded px-2 py-0.5 text-xs text-right outline-none w-full max-w-[150px]"
                                />
                              </div>
                              <div className="flex items-center gap-1.5 justify-end">
                                <span className="text-[10px] text-slate-400 font-semibold">الدم</span>
                                <select
                                  value={editPatientBloodType}
                                  onChange={(e) => setEditPatientBloodType(e.target.value)}
                                  className="border border-slate-300 bg-white rounded px-2 py-0.5 text-xs text-right outline-none w-full max-w-[150px]"
                                >
                                  <option value="">فصيلة الدم</option>
                                  <option value="A+">A+</option>
                                  <option value="A-">A-</option>
                                  <option value="B+">B+</option>
                                  <option value="B-">B-</option>
                                  <option value="AB+">AB+</option>
                                  <option value="AB-">AB-</option>
                                  <option value="O+">O+</option>
                                  <option value="O-">O-</option>
                                </select>
                              </div>
                              <div className="flex items-center gap-1.5 justify-end">
                                <span className="text-[10px] text-slate-400 font-semibold">الحساسية</span>
                                <input
                                  type="text"
                                  value={editPatientAllergies}
                                  onChange={(e) => setEditPatientAllergies(e.target.value)}
                                  placeholder="مثال: Penicillin"
                                  className="border border-slate-300 bg-white rounded px-2 py-0.5 text-xs text-right outline-none w-full max-w-[150px]"
                                />
                              </div>
                              <div className="flex items-center gap-1.5 justify-end mt-1">
                                <button
                                  type="button"
                                  onClick={() => handleSavePatientName(patient.id)}
                                  className="px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-[10px] font-bold"
                                >
                                  حفظ
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingPatientId(null)}
                                  className="px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-600 rounded text-[10px] font-bold"
                                >
                                  إلغاء
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span>{patient.name}</span>
                              <button 
                                onClick={() => {
                                  setEditingPatientId(patient.id);
                                  setEditPatientName(patient.name);
                                  setEditPatientPhone(patient.phone || "");
                                  setEditPatientNationalId(patient.nationalId || "");
                                  setEditPatientGender(patient.gender || "");
                                  setEditPatientDob(patient.dob || "");
                                  setEditPatientBloodType(patient.bloodType || "");
                                  setEditPatientAllergies(patient.allergies?.join(", ") || "");
                                }}
                                className="text-indigo-500 hover:text-indigo-700 text-[10px]"
                                title="تعديل بيانات المريض"
                              >
                                ✏️
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSendTelegram(patient.id);
                                }}
                                disabled={tgStatus[patient.id] === 'loading'}
                                className={`text-[11px] p-0.5 rounded transition shrink-0 inline-flex items-center justify-center ${
                                  tgStatus[patient.id] === 'loading' ? 'animate-pulse text-slate-400 bg-slate-50' :
                                  tgStatus[patient.id] === 'success' ? 'text-emerald-600 bg-emerald-50' :
                                  tgStatus[patient.id] === 'error' ? 'text-rose-600 bg-rose-50' :
                                  'text-sky-500 hover:text-sky-600 hover:bg-sky-50'
                                }`}
                                title="إرسال إشعار تليجرام"
                              >
                                {tgStatus[patient.id] === 'loading' ? (
                                  <span>⏳</span>
                                ) : tgStatus[patient.id] === 'success' ? (
                                  <span>✅</span>
                                ) : tgStatus[patient.id] === 'error' ? (
                                  <span>⚠️</span>
                                ) : (
                                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.02-1.96 1.24-5.54 3.65-.52.36-.99.53-1.41.52-.46-.01-1.35-.26-2.01-.48-.81-.27-1.46-.42-1.4-.88.03-.24.37-.48 1.02-.73 3.98-1.73 6.64-2.87 7.99-3.41 3.8-1.55 4.59-1.82 5.1-.1.11.24.18.52.2.82z"/>
                                  </svg>
                                )}
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-3 font-mono text-slate-500 text-[11px]">{patient.phone}</td>
                        <td className="py-3 px-3 text-slate-600 font-semibold">
                          {(appt.physicianId || "").toLowerCase().includes("ali") ? "طبيبة أسنان وتجميل - د. جمانة مصطفى الجلبي" :
                           
                           ((appt.physicianId || "").toLowerCase().includes("watheq") || (appt.physicianId || "").toLowerCase().includes("mytradefuture")) ? "Dr. Watheq Al-Chalabi" : (appt.physicianId || "")}
                        </td>
                        <td className="py-3 px-3">
                          <span className="bg-slate-100 text-slate-700 font-bold text-[8px] px-2 py-0.5 rounded uppercase font-mono border border-slate-200">
                            {appt.department}
                          </span>
                        </td>
                        <td className="py-3 px-3 font-mono text-slate-500">
                          {new Date(appt.dateTime).toLocaleString("ar-IQ", {
                            day: "numeric",
                            month: "numeric",
                            hour: "numeric",
                            minute: "2-digit"
                          })}
                        </td>
                        <td className="py-3 px-3 text-center">
                          {appt.status === "PENDING" ? (
                            <span className="bg-indigo-50 text-indigo-700 border border-indigo-150 font-bold text-[8.5px] uppercase px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-indigo-600 animate-pulse"></span>
                              Waiting / في الانتظار
                            </span>
                          ) : appt.status === "IN_PROGRESS" ? (
                            <span className="bg-amber-50 text-amber-700 border border-amber-150 font-bold text-[8.5px] uppercase px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                              In Progress / معلق
                            </span>
                          ) : appt.status === "MISSED" ? (
                            <span className="bg-rose-50 text-rose-700 border border-rose-150 font-bold text-[8.5px] uppercase px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-600"></span>
                              Missed / فائت
                            </span>
                          ) : (
                            <span className="bg-emerald-50 text-emerald-700 border border-emerald-150 font-bold text-[8.5px] uppercase px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                              Completed / اكتمل
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-right">
                          <div className="flex items-center gap-1.5 justify-end">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingApptId(appt.id);
                                // Get local datetime string suitable for datetime-local input value
                                const localDT = new Date(new Date(appt.dateTime).getTime() - new Date(appt.dateTime).getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                                setEditTime(localDT);
                                setEditStatus(appt.status as any);
                                setEditPhysician(appt.physicianId || "");
                                setEditDept(appt.department as any);
                              }}
                              className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 font-bold text-[10.5px] rounded px-2.5 py-1 cursor-pointer transition select-none"
                            >
                              تعديل
                            </button>
                            <div className="scale-95 origin-right">
                              <SendWhatsAppButton
                                patientPhone={patient.phone}
                                messageContent={`🩺 *تأكيد حجز الدكتور واثق الجلبي (IQ-Doctor)*\n\n👤 *المريض:* ${patient.name}\n📅 *موعد الحجز:* ${new Date(appt.dateTime).toLocaleString("ar-IQ", { day: "numeric", month: "numeric", hour: "numeric", minute: "2-digit" })}\n🩺 *العيادة:* ${appt.department === "DERMATOLOGY" ? "الجلدية والليزر" : "الطب العام والباطني"}\n👨‍⚕️ *الطبيب:* ${(appt.physicianId || "").toLowerCase().includes("ali") ? "د. جمانة مصطفى الجلبي" : "Dr. Watheq Al-Chalabi"}\n\n📍 العراق - بغداد - السيدية\n📞 للاستفسار وتأكيد الحضور يرجى التواصل معنا.\n🔒 نظام لإدارة العيادات الطبية الآمن`}
                                label="Booking"
                              />
                            </div>
                            <button
                              type="button"
                              id={`print-sticker-btn-${appt.id}`}
                              onClick={() => {
                                setPrintingWristband({
                                  patientName: patient.name,
                                  dob: patient.dob,
                                  dept: appt.department,
                                  time: new Date(appt.dateTime).toLocaleString()
                                });
                              }}
                              className="bg-white hover:bg-slate-50 border border-slate-200 text-slate-705 hover:text-indigo-600 font-bold inline-flex items-center gap-1 text-[10.5px] rounded px-3 py-1 cursor-pointer transition select-none"
                            >
                              <Printer className="w-3.5 h-3.5" />
                              <span>طباعة ستيكر</span>
                            </button>
                            <button
                              type="button"
                              id={`print-receipt-btn-${appt.id}`}
                              onClick={() => setPrintingBill(patient)}
                              className="bg-white hover:bg-emerald-50 border border-slate-200 text-emerald-700 hover:text-emerald-800 font-bold inline-flex items-center gap-1 text-[10.5px] rounded px-3 py-1 cursor-pointer transition select-none"
                            >
                              <Printer className="w-3.5 h-3.5" />
                              <span>وصل Dr.watheq AlChalabi</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        ) : (
          <div id="active-queue-empty" className="text-center py-12 border border-dashed border-[#E2E8F0] rounded-md bg-slate-50/20">
            <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <h4 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider text-[#64748B]">No Reservations Logged Today</h4>
            <p className="text-[#64748B] text-[11px] mt-1 max-w-[280px] mx-auto leading-relaxed">
              Active patient appointments registered will show here to track clinical status and trigger label printers.
            </p>
          </div>
        )}
      </div>

      {/* Financials & Installments Reception Component */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm mb-6 mt-6">
        <h3 className="font-bold text-[#1E293B] text-sm mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded bg-emerald-100 flex items-center justify-center">
            <span className="text-emerald-700 font-black text-xs">IQD</span>
          </span>
          الحسابات والأقساط المستحقة (Billing & Installments)
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-right border-collapse">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase font-bold tracking-wider">
                <th className="py-2 px-3 border-b border-slate-200">المريض</th>
                <th className="py-2 px-3 border-b border-slate-200">الرقم المالي (ID)</th>
                <th className="py-2 px-3 border-b border-slate-200">مبلغ القسط</th>
                <th className="py-2 px-3 border-b border-slate-200">تاريخ الاستحقاق</th>
                <th className="py-2 px-3 border-b border-slate-200 text-center">إجراءات الاستلام</th>
              </tr>
            </thead>
            <tbody>
              {financials
                .filter(f => f.isInstallmentBased && f.installments && f.installments.some(i => i.status === "PENDING"))
                .map(f => (
                  <React.Fragment key={f.id}>
                    {f.installments?.filter(i => i.status === "PENDING").map(inst => (
                      <tr key={inst.id} className="border-b border-slate-100 hover:bg-slate-50 transition">
                        <td className="py-2 px-3 font-bold text-xs text-slate-800">{f.patientName}</td>
                        <td className="py-2 px-3 text-[10px] font-mono text-slate-500">{inst.id}</td>
                        <td className="py-2 px-3 font-bold text-rose-600 text-xs">{inst.amount.toLocaleString()} د.ع</td>
                        <td className="py-2 px-3 font-mono text-xs">{new Date(inst.dueDate).toLocaleDateString("ar-IQ")}</td>
                        <td className="py-2 px-3 text-center">
                          <button
                            onClick={() => handlePayInstallment(f.id, inst.id)}
                            className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold text-[10px] px-3 py-1 rounded border border-emerald-200 transition"
                          >
                            استلام الدفعة وتسديد
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              {!financials.some(f => f.isInstallmentBased && f.installments && f.installments.some(i => i.status === "PENDING")) && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-slate-400 text-xs font-medium">
                    لا توجد أقساط مالية مستحقة الدفع حالياً.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {printingBill && (
        <PatientEhrPrintPreview
          patient={printingBill}
          vitals={null}
          clinicalRecords={[]}
          orders={[]}
          medicalHistory={null}
          docType="BILL_10000"
          onClose={() => setPrintingBill(null)}
        />
      )}
    </div>
  );
}
